import { z } from 'zod';
/**
 * Note — a personal, multi-media writing surface that can be attached
 * to zero or more entities (person, customer, topic, product, generic
 * graph entity). Markdown body via TipTap on the client (serialized
 * to GFM); a flat `attachments` array carries images, files, and voice
 * memos with AI-generated transcripts.
 *
 * Multi-tenant: every doc carries `orgId`; rules gate read/write to
 * the note's author. Notes are never shared cross-org and never
 * cross-author within an org (the agent's synthesis pass does NOT
 * read private notes; an opt-in toggle is on the post-MVP roadmap).
 *
 * Multi-target model: a note can be attached to N entities via
 * `targets[]`. Empty `targets[]` means a standalone personal note
 * (the /notes inbox is the only place it surfaces). `targetEntityIds`
 * is the server-derived flat union of `targets[].entityId` and any
 * `@`-mentions parsed from the body — it powers dossier-rail queries
 * via `array-contains`.
 *
 * Why one collection, not per-target subcollections: cross-cutting
 * "all my notes" reads (the /notes inbox) are common, the dossier-rail
 * filter is `array-contains` on a single denormalized field, and a
 * flat collection with the (orgId, authorUid, ...) composite indexes
 * serves both query shapes cheaply.
 */
export const NOTES_COLLECTION = 'notes';
/**
 * Kind of entity a note can hang off. Maps 1:1 to the dossier surfaces
 * in the portal. `entity` is a catch-all for graph entities the
 * dossier surfaces beyond the named kinds. There's no `'standalone'`
 * member — a note with `targets: []` IS the standalone case.
 */
export const NoteTargetType = z.enum(['person', 'customer', 'topic', 'product', 'entity']);
/**
 * One entity the note is attached to. `entityId` is the canonical id
 * of the dossiered entity; resolution to a display name happens
 * client-side via `<EntityName>`. `entitySnapshotName` is captured at
 * note-save time so the chip still renders something readable if the
 * entity is later renamed or deleted.
 */
export const NoteTarget = z.object({
    type: NoteTargetType,
    entityId: z.string().min(1),
    entitySnapshotName: z.string().optional(),
});
/**
 * Attachment kinds. `image` and `file` cover paste/drop into the editor;
 * `voice-memo` covers in-app recordings (browser MediaRecorder), which
 * trigger Gemini transcription post-upload.
 */
export const NoteAttachmentKind = z.enum(['image', 'file', 'voice-memo']);
/**
 * A single attachment on a note. Stored in Cloud Storage at a path the
 * server controls (`noteAttachments/{orgId}/{noteId}/{uuid}.{ext}`); the
 * client gets a download URL via `getDownloadURL` and renders it inline
 * (image) or as a file chip (file/voice-memo).
 *
 * Voice-memo attachments carry `transcript` + `transcriptModel` once the
 * server-side transcription pass completes. The client polls (or
 * subscribes to the parent note doc) for transcript appearance.
 */
export const NoteAttachment = z.object({
    /** Stable id for the attachment (uuid). Used in markdown body refs. */
    id: z.string().min(1),
    kind: NoteAttachmentKind,
    /** Cloud Storage object path (the bucket is implicit). */
    storagePath: z.string().min(1),
    /** Original filename as supplied by the client. */
    filename: z.string().min(1),
    /** MIME type (`image/png`, `audio/webm`, etc.). */
    mimeType: z.string().min(1),
    /** Size in bytes. */
    sizeBytes: z.number().int().nonnegative(),
    /** ISO timestamp when the attachment finished uploading. */
    uploadedAt: z.string(),
    /**
     * Voice-memo only: transcribed text. Absent when transcription is
     * still pending or failed. Empty string is a valid "transcript was
     * empty" outcome (audio without speech).
     */
    transcript: z.string().optional(),
    /** Voice-memo only: model id that produced the transcript. */
    transcriptModel: z.string().optional(),
    /** Voice-memo only: duration in seconds. */
    durationSec: z.number().nonnegative().optional(),
    /** Image only: rendered width/height (for layout-stable rendering). */
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
/**
 * Per-field caps. Shared between `Note` (read shape), `CreateNoteRequest`,
 * and `UpdateNoteRequest` so a single source-of-truth governs every
 * write path.
 *
 *   - `body` 1 MB cap — generous enough for long-form notes including
 *     pasted articles, but bounds the DoS surface (Firestore doc cap
 *     is ~1 MiB anyway, so this is a hard physical limit too).
 *   - `attachments` cap of 200 — multi-media notes, but a thread of
 *     1000+ images is more "media library" than "note".
 *   - `targets` cap of 16 — explicit attachments. Body `@`-mentions
 *     don't count toward this; they're folded into `targetEntityIds`
 *     server-side.
 *   - `targetEntityIds` cap of 100 — generous union of explicit
 *     targets + body mentions. Hard cap so a single pathological note
 *     can't blow out the dossier-rail query for every mentioned entity.
 *   - `tags` and per-tag length: lightweight personal-organization
 *     surface, not an entity-classification system.
 */
export const NOTE_BODY_MAX = 1_000_000;
export const NOTE_ATTACHMENTS_MAX = 200;
export const NOTE_TARGETS_MAX = 16;
export const NOTE_TARGET_ENTITY_IDS_MAX = 100;
export const NOTE_TAGS_MAX = 32;
export const NOTE_TAG_MAX_LEN = 64;
export const NOTE_TITLE_MAX = 200;
/**
 * The Note entity itself.
 *
 * `body` is GFM markdown — TipTap on the client serializes its
 * ProseMirror doc to markdown for storage. Server stores verbatim;
 * any sanitization happens client-side at render. Markdown can
 * embed attachment refs as `![alt](attachment://<attachmentId>)` for
 * images or `[label](attachment://<attachmentId>)` for files.
 *
 * `@`-mentions of entities serialize as
 *     `[@Display Name](leanopx://entity/<type>/<entityId>)`
 * — both the type and entityId live in the URL path so the server
 * parser is regex-friendly + the client renderer can hand the parts
 * to `<EntityName>` for display.
 */
export const Note = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    authorUid: z.string().min(1),
    /** Optional display title; falls back to first-line-of-body in the UI. */
    title: z.string().max(NOTE_TITLE_MAX).optional(),
    /** GFM markdown body. Empty string is allowed (attachment-only notes). */
    body: z.string().max(NOTE_BODY_MAX),
    /**
     * Entities the note is explicitly attached to (via the editor's
     * "Attach to…" affordance OR by being created from a dossier rail).
     * Empty array = standalone personal note.
     */
    targets: z.array(NoteTarget).max(NOTE_TARGETS_MAX).default([]),
    /**
     * Server-derived flat union of `targets[].entityId` and `@`-mentioned
     * entity ids parsed from `body`. Powers dossier-rail queries via
     * `array-contains`. Clients should not write this directly — the
     * server recomputes it on every mutation.
     */
    targetEntityIds: z.array(z.string().min(1)).max(NOTE_TARGET_ENTITY_IDS_MAX).default([]),
    attachments: z.array(NoteAttachment).max(NOTE_ATTACHMENTS_MAX).default([]),
    /**
     * Free-form tags for personal organization (`#prep`, `#ic-feedback`).
     * Different concept from signal-topic tags; lives only on the note
     * surface.
     */
    tags: z.array(z.string().max(NOTE_TAG_MAX_LEN)).max(NOTE_TAGS_MAX).default([]),
    /** ISO; when the note was first created. */
    createdAt: z.string(),
    /** ISO; mutates on every body/attachment change. */
    updatedAt: z.string(),
});
/**
 * Body for `POST /api/orgs/:o/notes`. Server stamps id/timestamps/author
 * and recomputes `targetEntityIds` from `targets[]` + body-parsed
 * mentions. The server ALWAYS re-parses the body — `mentionedEntityIds`
 * is treated as a hint that gets intersected against the parser's
 * output (any client-supplied id not actually present in the body is
 * dropped). This defends against a buggy or hostile client over-claiming
 * mentions, AND keeps the wire shape forward-compatible if a future
 * client wants to volunteer the id list explicitly.
 */
export const CreateNoteRequest = z.object({
    title: z.string().max(NOTE_TITLE_MAX).optional(),
    body: z.string().max(NOTE_BODY_MAX).default(''),
    targets: z.array(NoteTarget).max(NOTE_TARGETS_MAX).default([]),
    /**
     * Optional client-supplied list of entity ids the editor inserted
     * via `@`-mention. Filtered server-side against the actual body
     * mentions — over-claims are dropped. Useful for forward-compat and
     * for clients that already have the list in hand from the editor's
     * ProseMirror state; never trusted past the filter.
     */
    mentionedEntityIds: z.array(z.string().min(1)).max(NOTE_TARGET_ENTITY_IDS_MAX).optional(),
    tags: z.array(z.string().max(NOTE_TAG_MAX_LEN)).max(NOTE_TAGS_MAX).default([]),
    /**
     * Initial attachments — typically empty on create. The voice-memo
     * recorder uploads via a separate endpoint that returns the persisted
     * attachment record.
     */
    attachments: z.array(NoteAttachment).max(NOTE_ATTACHMENTS_MAX).default([]),
});
/** Body for `PATCH /api/orgs/:o/notes/:id`. Partial; server merges. */
export const UpdateNoteRequest = z.object({
    title: z.string().max(NOTE_TITLE_MAX).nullable().optional(),
    body: z.string().max(NOTE_BODY_MAX).optional(),
    /**
     * Replace the full targets array. Use `null` semantics via the
     * `[]`-empty value — there's no per-target add/remove endpoint;
     * the editor is the only consumer and it always knows the full set.
     */
    targets: z.array(NoteTarget).max(NOTE_TARGETS_MAX).optional(),
    mentionedEntityIds: z.array(z.string().min(1)).max(NOTE_TARGET_ENTITY_IDS_MAX).optional(),
    tags: z.array(z.string().max(NOTE_TAG_MAX_LEN)).max(NOTE_TAGS_MAX).optional(),
    /**
     * Replaces the full attachments array when provided. Use the
     * dedicated `/attachments` and `/attachments/:id` endpoints for
     * single-attachment add/delete; the wholesale shape is here for
     * editor flows that re-order or remove inline.
     */
    attachments: z.array(NoteAttachment).max(NOTE_ATTACHMENTS_MAX).optional(),
});
/**
 * Body for the upload-prep endpoint
 * `POST /api/orgs/:o/notes/:id/attachments/prepare`. The server returns
 * a signed-upload URL + the storagePath the client should use; the
 * client uploads directly to Cloud Storage, then calls the finalize
 * endpoint to attach the record to the note.
 *
 * Two-step upload pattern (vs. proxying through the function) saves
 * the function 540s timeouts on large files and avoids burning gen-2
 * memory on bulk uploads.
 */
export const PrepareAttachmentRequest = z.object({
    kind: NoteAttachmentKind,
    filename: z.string().min(1).max(512),
    mimeType: z.string().min(1).max(128),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(100 * 1024 * 1024), // 100 MB cap
});
export const PrepareAttachmentResponse = z.object({
    attachmentId: z.string().min(1),
    storagePath: z.string().min(1),
    /** Signed PUT URL valid for ~15 minutes. */
    uploadUrl: z.string().url(),
    /** Headers the client must include on the PUT. */
    uploadHeaders: z.record(z.string(), z.string()),
});
/**
 * Body for `POST /api/orgs/:o/notes/:id/attachments/:attachmentId/finalize`.
 * The client calls this after the Storage upload completes; the server
 * verifies the object exists, snaps the size + timestamps from Storage,
 * and (for voice-memo) kicks off the transcription pass.
 */
export const FinalizeAttachmentRequest = z.object({
    /** Optional client-side metadata captured during upload. */
    durationSec: z.number().nonnegative().optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
/** Wire shape returned by `GET /api/orgs/:o/notes`. */
export const ListNotesResponse = z.object({
    notes: z.array(Note),
});
/**
 * Mention serialization helpers. `@`-mentions in the markdown body
 * use the `leanopx://entity/<type>/<entityId>` URL scheme inside a
 * standard markdown link, which keeps the source readable and lets
 * the server parser pull both type + id with a single regex.
 *
 * The display label inside the link is the entity name at mention
 * time; if the entity is later renamed, the snapshot stays.
 */
export const MENTION_URL_PREFIX = 'leanopx://entity/';
/**
 * Match `[@Label](leanopx://entity/<type>/<entityId>)` anywhere in
 * the markdown body. Capture groups: 1=label, 2=type, 3=entityId.
 *
 * Entity ids are URL-encoded by the client serializer to keep the
 * `/` separator unambiguous; consumers should `decodeURIComponent`
 * the captured value.
 */
export const MENTION_REGEX = /\[@([^\]]+)\]\(leanopx:\/\/entity\/([a-z]+)\/([^)]+)\)/g;
/**
 * Server-side parser: walk a markdown body and return the unique set
 * of `@`-mentioned entity ids. Order-preserving by first appearance
 * so the server can pair `(type, id)` deterministically when it
 * needs to re-derive `targets[]` (it doesn't today, but future
 * automation might).
 *
 * Malformed percent-encoding (e.g. `%ZZ`) on a captured id is
 * treated as a non-match — the offending capture is skipped rather
 * than thrown, so a hostile or buggy markdown body can't 500 the
 * note create/update path.
 */
export function parseMentionedEntityIds(body) {
    const out = [];
    const seen = new Set();
    for (const match of body.matchAll(MENTION_REGEX)) {
        let id;
        try {
            id = decodeURIComponent(match[3] ?? '');
        }
        catch {
            // URIError on malformed sequences — skip this mention entirely.
            continue;
        }
        if (!id || seen.has(id))
            continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}
